import { Link } from 'react-router-dom';
import { FileText, MapPin, HelpCircle, Hammer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { WizardStepper } from '@/components/custom/WizardStepper';

const ticketTypes = [
  {
    to: '/tickets/new/open-order',
    icon: FileText,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    titleKey: 'OPEN_ORDER',
    descKey: 'openOrderDesc',
    ticketType: 'OPEN_ORDER',
  },
  {
    to: '/tickets/new/address',
    icon: MapPin,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
    titleKey: 'ADDRESS_MANAGEMENT',
    descKey: 'addressDesc',
    ticketType: 'ADDRESS_MANAGEMENT',
  },
  {
    to: '/tickets/new/support',
    icon: HelpCircle,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    titleKey: 'SUPPORT_REQUEST',
    descKey: 'supportDesc',
    ticketType: 'SUPPORT_REQUEST',
  },
  {
    to: '/tickets/new/build',
    icon: Hammer,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    titleKey: 'BUILD',
    descKey: 'buildDesc',
    ticketType: 'BUILD',
  },
];

/**
 * Column count for the tile grid. Tiles are permission-filtered, so any count from 1 to 4 is
 * reachable; four wrap to a 2x2 rather than squeezing into one row.
 */
const GRID_COLS: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-2',
};

export function TicketTypeSelector() {
  const { user } = useAuth();
  const { t } = useTranslation(['tickets', 'common']);

  const canCreate = user?.effectivePermissions?.canCreate ?? [];
  const visibleTypes = ticketTypes.filter((tt) => canCreate.includes(tt.ticketType));

  return (
    <div className="max-w-4xl mx-auto">
      {/* Step indicator */}
      <WizardStepper
        steps={[t('tickets:create.steps.selectType'), t('tickets:create.steps.details'), t('tickets:create.steps.review')]}
        currentStep={0}
      />

      {/* Type selection */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mt-8">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-slate-900">{t('tickets:create.selectType')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('tickets:create.selectTypeHint')}</p>
        </div>

        {visibleTypes.length === 0 ? (
          <p className="text-center text-slate-400 py-8">
            {t('tickets:create.noTypes')}
          </p>
        ) : (
          <div className={`grid grid-cols-1 ${GRID_COLS[visibleTypes.length] ?? 'md:grid-cols-2'} gap-4 max-w-2xl mx-auto`}>
            {visibleTypes.map((type) => {
              const Icon = type.icon;
              return (
                <Link
                  key={type.to}
                  to={type.to}
                  className="bg-white rounded-xl border-2 border-slate-200 p-6 text-center hover:border-brand-rose hover:shadow-md transition-all group"
                >
                  <div className={`w-14 h-14 rounded-xl ${type.iconBg} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 ${type.iconColor}`} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1">{t(`common:ticketType.${type.titleKey}`)}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{t(`tickets:create.${type.descKey}`)}</p>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Cancel */}
      <div className="flex justify-start mt-6">
        <Link
          to="/tickets"
          className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          {t('common:actions.cancel')}
        </Link>
      </div>
    </div>
  );
}
